import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { formatDate } from "../../api/dates";
import { formatMoney, formatPercent } from "../../api/money";
import { LIST_KEY } from "../../api/quotations";
import DropZone from "./reader/DropZone";
import InspectionPicker from "./reader/InspectionPicker";
import IssueList from "./reader/IssueList";
import LinesTable, { type Resolution } from "./reader/LinesTable";
import OverrideCheck from "./reader/OverrideCheck";
import {
  ApiError,
  fileAsBase64,
  readPdf,
  saveQuotation,
  type JobTarget,
  type LineToSave,
  type Review,
} from "./reader/readerApi";
import {
  blockingLeft,
  canOverride,
  holdAt,
  initialTarget,
  lineSummary,
  overridableLeft,
  READER_STEPS,
  toSave,
  type ReaderStep,
  warnings,
} from "./reader/review";
import StartQuotation from "./StartQuotation";

const READ_URL = "/api/quotations/read";
const SAVE_URL = "/api/quotations/read/save";

interface Props {
  onClose: () => void;
  onSaved: (quotationId: number) => void;
}

interface Loaded {
  file: File;
  review: Review;
}

/** Reading a supplier's quotation, as a drawer over the commercial screen in
 * four steps: read the PDF, check its lines against the price list, pick the
 * inspection it prices, save it as a draft (Fix #13). It used to be squeezed
 * into a column beside the start form, with an eight-column table in a third
 * of the width; each step now has the drawer to itself, and the next is
 * offered only once this one is settled. */
export default function ReaderDrawer({ onClose, onSaved }: Props) {
  const client = useQueryClient();
  const [step, setStep] = useState<ReaderStep>(1);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [resolutions, setResolutions] = useState<Record<number, Resolution>>({});
  const [target, setTarget] = useState<JobTarget | null>(null);
  // False while the job is the one read from the quote: the office confirms
  // it, or picks another, before the reader goes on.
  const [confirmed, setConfirmed] = useState(true);
  const [byHand, setByHand] = useState(false);
  // Going on in spite of the totals check: chosen, and why. Only the reason
  // counts -- an override with none still holds the drawer.
  const [overriding, setOverriding] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    heading.current?.focus();
  }, []);

  const onDrop = useCallback(async (file: File) => {
    setReading(true);
    setError("");
    setResolutions({});
    setTarget(null);
    setOverriding(false);
    setOverrideReason("");
    try {
      const review = await readPdf(READ_URL, file);
      setLoaded({ file, review });
      const pointedAt = initialTarget(review);
      setTarget(pointedAt);
      setConfirmed(pointedAt === null);
    } catch (cause) {
      setLoaded(null);
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setReading(false);
    }
  }, []);

  // The dropped PDF as a link the office can open beside the lines. It exists
  // only in this browser until the quotation is saved, so it is a local URL,
  // let go when another file is dropped or the drawer closes.
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  useEffect(() => {
    if (loaded === null || typeof URL.createObjectURL !== "function") {
      setPdfUrl(null);
      return;
    }
    // Typed as a PDF whatever the browser said the dropped file was: a file with
    // no type would download instead of opening.
    const url = URL.createObjectURL(new Blob([loaded.file], { type: "application/pdf" }));
    setPdfUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [loaded]);

  const review = loaded?.review ?? null;
  const override = overriding ? overrideReason.trim() : "";
  const hold = holdAt(step, review, resolutions, target, confirmed, override);

  async function save() {
    if (loaded === null || loaded.review.supplier === null || target === null) {
      return;
    }
    const lines = loaded.review.lines.map((line) => toSave(line, resolutions[line.seq]));
    const ready = lines.filter((line): line is LineToSave => line !== null);
    if (ready.length !== lines.length) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const q = loaded.review.quotation;
      const saved = await saveQuotation(SAVE_URL, {
        supplier_id: loaded.review.supplier.id,
        inspection_id: target.kind === "inspection" ? target.id : null,
        site_id: target.kind === "site" ? target.id : null,
        number: q.number,
        quote_date: q.quote_date || null,
        valid_until: q.valid_to || null,
        document_discount_pct: q.discount_pct,
        lines: ready,
        attachment_base64: await fileAsBase64(loaded.file),
        overridden: override === "" ? [] : overridableLeft(loaded.review).map((issue) => issue.code),
        override_reason: override === "" ? null : override,
      });
      void client.invalidateQueries({ queryKey: LIST_KEY });
      onSaved(saved.quotation_id);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  }

  function next() {
    if (hold !== null) {
      return;
    }
    if (step < 4) {
      setStep((step + 1) as ReaderStep);
      return;
    }
    void save();
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="drawer wide qr-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reader-title"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onClose();
          }
        }}
      >
        <div className="drawer-head qr-drawer-head">
          <div className="qr-drawer-title">
            <h2 id="reader-title" ref={heading} tabIndex={-1}>
              Read a supplier's quotation
            </h2>
            <button type="button" className="btn drawer-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
          <ol className="qr-steps" aria-label="Steps">
            {READER_STEPS.map((item) => {
              const state = item.step < step ? "done" : item.step === step ? "current" : "todo";
              return (
                <li key={item.step} className={`qr-step ${state}`} aria-current={state === "current" ? "step" : undefined}>
                  <span className="qr-step-dot" aria-hidden="true">
                    {state === "done" ? "✓" : item.step}
                  </span>
                  <span className="qr-step-label">{item.label}</span>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="drawer-body qr-drawer-body">
          {error === "" ? null : (
            <p className="notice bad" role="alert">
              {error}
            </p>
          )}
          {step === 1 ? (
            <ReadStep
              loaded={loaded}
              reading={reading}
              onDrop={onDrop}
              byHand={byHand}
              onByHand={() => setByHand((now) => !now)}
              onStarted={onSaved}
            />
          ) : null}
          {step === 2 && review !== null ? (
            <CheckStep
              review={review}
              resolutions={resolutions}
              onResolve={setResolution(setResolutions)}
              overriding={overriding}
              overrideReason={overrideReason}
              onOverriding={setOverriding}
              onOverrideReason={setOverrideReason}
              pdfUrl={pdfUrl}
            />
          ) : null}
          {step === 3 && review !== null ? (
            <>
              <p className="cm-doc-note">
                A quotation prices one job. Pick a submitted inspection{" "}
                {review.supplier?.name ?? "this supplier"} hasn&rsquo;t quoted yet, or allocate it to its site.
              </p>
              <InspectionPicker
                candidates={review.candidates}
                supplier={review.supplier?.name ?? "this supplier"}
                quotedSite={review.quotation.site}
                value={target}
                onChange={(chosen) => {
                  setTarget(chosen);
                  setConfirmed(true);
                }}
                workOrder={review.work_order}
                confirmed={confirmed}
                onConfirm={() => setConfirmed(true)}
              />
            </>
          ) : null}
          {step === 4 && review !== null ? (
            <SaveStep review={review} resolutions={resolutions} target={target} override={override} />
          ) : null}
        </div>

        <div className="qr-drawer-foot">
          {step > 1 ? (
            <button type="button" className="btn" onClick={() => setStep((step - 1) as ReaderStep)} disabled={saving}>
              Back
            </button>
          ) : null}
          <span className="qr-hint">{hold ?? ""}</span>
          <button type="button" className="btn btn-primary" disabled={hold !== null || saving || reading} onClick={next}>
            {step === 4 ? (saving ? "Saving…" : "Save as draft quotation") : "Continue"}
          </button>
        </div>
      </div>
    </>
  );
}

function setResolution(set: (update: (now: Record<number, Resolution>) => Record<number, Resolution>) => void) {
  return (seq: number, resolution: Resolution | null) =>
    set((now) => {
      const rest = Object.fromEntries(Object.entries(now).filter(([key]) => Number(key) !== seq));
      return resolution === null ? rest : { ...rest, [seq]: resolution };
    });
}

function ReadStep({
  loaded,
  reading,
  onDrop,
  byHand,
  onByHand,
  onStarted,
}: {
  loaded: Loaded | null;
  reading: boolean;
  onDrop: (file: File) => void;
  byHand: boolean;
  onByHand: () => void;
  onStarted: (quotationId: number) => void;
}) {
  const review = loaded?.review;
  const q = review?.quotation;
  return (
    <>
      <DropZone onFile={onDrop} busy={reading} fileName={loaded?.file.name ?? null} />
      {review && q ? (
        <section className="qr-summary" aria-label="What was read">
          <div className="qr-summary-head">
            <strong>
              {q.read_as} quotation {q.number}
            </strong>
            <span>
              {review.supplier
                ? `${review.supplier.name} (${review.supplier.acc_no}) · ${formatPercent(review.supplier.discount_pct)} standing discount`
                : "Supplier not on the providers list"}
            </span>
          </div>
          <dl className="cm-facts">
            <div>
              <dt>Dated</dt>
              <dd>{formatDate(q.quote_date || null)}</dd>
            </div>
            <div>
              <dt>Valid to</dt>
              <dd>{formatDate(q.valid_to || null)}</dd>
            </div>
            <div>
              <dt>Site named</dt>
              <dd>{q.site || "None"}</dd>
            </div>
            <div>
              <dt>Work order</dt>
              <dd className="mono">{review.work_order?.number ?? "Not stated"}</dd>
            </div>
            <div>
              <dt>Discount printed</dt>
              <dd>{formatPercent(q.discount_pct)}</dd>
            </div>
          </dl>
          <p className="qr-ok">{lineSummary(review.lines)}</p>
        </section>
      ) : null}
      <div className="qr-byhand">
        <button type="button" className="go-link" onClick={onByHand} aria-expanded={byHand}>
          {byHand ? "Hide" : "No PDF? Capture the quotation by hand"}
        </button>
        {byHand ? <StartQuotation onStarted={onStarted} /> : null}
      </div>
    </>
  );
}

function CheckStep({
  review,
  resolutions,
  onResolve,
  overriding,
  overrideReason,
  onOverriding,
  onOverrideReason,
  pdfUrl,
}: {
  review: Review;
  resolutions: Record<number, Resolution>;
  onResolve: (seq: number, resolution: Resolution | null) => void;
  overriding: boolean;
  overrideReason: string;
  onOverriding: (overriding: boolean) => void;
  onOverrideReason: (reason: string) => void;
  pdfUrl: string | null;
}) {
  const override = overriding ? overrideReason.trim() : "";
  const blocking = blockingLeft(review, resolutions, override).length;
  const warned = warnings(review);
  const q = review.quotation;
  return (
    <>
      <div className="pill-row">
        <span className={blocking === 0 ? "status-chip tone-ok" : "status-chip tone-crit"}>{blocking} blocking</span>
        <span className={warned === 0 ? "status-chip" : "status-chip tone-warn"}>
          {warned} warning{warned === 1 ? "" : "s"}
        </span>
        {override !== "" ? (
          <span className="status-chip tone-warn">{overridableLeft(review).length} overridden</span>
        ) : null}
      </div>
      <IssueList issues={review.issues.filter((issue) => issue.line_seq === null)} overridden={override !== ""} />
      {canOverride(review) ? (
        <OverrideCheck
          overriding={overriding}
          reason={overrideReason}
          onOverriding={onOverriding}
          onReason={onOverrideReason}
          pdfUrl={pdfUrl}
        />
      ) : null}
      <LinesTable
        lines={review.lines}
        catalogue={review.catalogue}
        issues={review.issues}
        resolutions={resolutions}
        onResolve={onResolve}
      />
      <p className="cm-doc-note">
        Quotation prints {formatMoney(q.printed_subtotal)} excl VAT · {formatMoney(q.printed_vat)} VAT ·{" "}
        {formatMoney(q.printed_total)} incl. Lines are net of {review.supplier?.name ?? "the supplier"}'s discount, as
        the price list is.
      </p>
    </>
  );
}

function SaveStep({
  review,
  resolutions,
  target,
  override,
}: {
  review: Review;
  resolutions: Record<number, Resolution>;
  target: JobTarget | null;
  override: string;
}) {
  const candidate =
    target?.kind === "inspection" ? review.candidates.find((c) => c.inspection_id === target.id) : undefined;
  const resolved = review.lines.filter((line) => line.kind === "unmatched");
  const warned = review.issues.filter((issue) => issue.severity === "warning");
  return (
    <>
      <ul className="qr-checks">
        <li className="ok">{lineSummary(review.lines)}</li>
        {resolved.map((line) => {
          const resolution = resolutions[line.seq];
          return (
            <li key={line.seq} className="ok">
              Line {line.seq}{" "}
              {resolution?.kind === "part" ? `resolved as ${resolution.part.code} ${resolution.part.description}` : "kept as a sundry, as quoted"}
            </li>
          );
        })}
        {override === ""
          ? null
          : overridableLeft(review).map((issue) => (
              <li key={`override-${issue.code}`} className="warn">
                Overridden: {issue.message} Your reason, kept on its history: &ldquo;{override}&rdquo;
              </li>
            ))}
        {warned.map((issue, index) => (
          <li key={`${issue.code}-${index}`} className="warn">
            {issue.line_seq === null ? "" : `Line ${issue.line_seq}: `}
            {issue.message}
          </li>
        ))}
        <li className="ok">
          {target?.kind === "site"
            ? `For ${target.name}${target.workOrder ? `, work order ${target.workOrder}` : ""}: it joins the site's open job, or opens one for the field operators to complete`
            : `For works order ${candidate ? `${candidate.work_order_no}, ${candidate.site || "site not named"}` : "—"}`}
        </li>
      </ul>
      <p className="notice">
        It saves as a draft quotation on the job, with the supplier's PDF attached. You can still amend lines before
        submitting it for approval.
      </p>
    </>
  );
}
